import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, MenuItem, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../contexts/AuthContext';
import { getAcceptLanguageHeader, useI18n } from '../i18n';
import OrderReceipt from '../components/OrderReceipt';

const PDF_ACT_LANGS = [
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'Английский' },
];

const PRIMARY_FIELDS = [
  { key: 'order_status', label: 'Статус' },
  { key: 'external_order_id', label: 'Номер заказа' },
  { key: 'phone_dist', label: 'Номер пополнения' },
  { key: 'email', label: 'Email' },
  { key: 'payment_time', label: 'Дата и время оплаты' },
  { key: 'lang', label: 'Язык' },
  { key: 'payment_system', label: 'Платежная система' },
  { key: 'amount_dist', label: 'Сумма пополнения' },
  { key: 'amount_src', label: 'Сумма платежа' },
  { key: 'currency_src', label: 'Валюта' },
  { key: 'payment_method', label: 'Способ оплаты' },
  { key: 'service_name', label: 'Сервис' },
  { key: 'phone_src', label: 'Клиент' },
  { key: 'created_at', label: 'Дата создания' },
];

function formatValue(key, value) {
  if (value === null || value === undefined || value === '') return '-';
  if (key === 'payment_time' || key === 'created_at') {
    const d = new Date(String(value).replace(' ', 'T'));
    if (!isNaN(d.getTime())) return d.toLocaleString();
  }
  if (key === 'lang') {
    const str = String(value);
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : '-';
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default function FindOrderPage() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState('');
  const [hasPhoneSearched, setHasPhoneSearched] = useState(false);
  const [selectedResultId, setSelectedResultId] = useState(null);
  const [resultsPage, setResultsPage] = useState(0);
  const [resultsPerPage, setResultsPerPage] = useState(20);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [lastPhoneDistQuery, setLastPhoneDistQuery] = useState('');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifySeverity, setNotifySeverity] = useState('success');
  const [serviceActLoading, setServiceActLoading] = useState(false);
  const [serviceActLang, setServiceActLang] = useState('ru');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  const primaryFieldKeys = useMemo(() => new Set(PRIMARY_FIELDS.map(item => item.key)), []);

  const rows = useMemo(() => {
    if (!order) return [];
    return PRIMARY_FIELDS.map(item => ({
      label: t(item.label),
      value: formatValue(item.key, order[item.key]),
      key: item.key,
    }));
  }, [order, t]);

  const extraRows = useMemo(() => {
    if (!order) return [];
    return Object.entries(order)
      .filter(([key]) => !primaryFieldKeys.has(key))
      .map(([key, value]) => ({
        label: key,
        value: formatValue(key, value),
        key,
      }));
  }, [order, primaryFieldKeys]);

  const loadOrderDetails = async (number) => {
    if (!user?.api_token) {
      setError(t('Нет доступа'));
      return;
    }
    setLoading(true);
    setError('');
    setHasSearched(true);
    try {
      const res = await fetch(`/api/orders/find?number=${encodeURIComponent(number)}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${user.api_token}`,
          'Accept-Language': getAcceptLanguageHeader(),
        },
      });
      const payload = await res.json().catch(() => ({}));
      if (res.status === 401 || res.status === 403) {
        logout();
        throw new Error(t('Нет доступа'));
      }
      if (!res.ok) {
        throw new Error(payload.error || t('Заказ не найден'));
      }
      setOrder(payload.order || null);
      if (!payload.order) {
        setError(t('Заказ не найден'));
      }
    } catch (err) {
      setOrder(null);
      setError(err?.message || t('Ошибка поиска заказа'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const trimmed = orderNumber.trim();
    if (!trimmed) {
      setError(t('Введите номер заказа'));
      setOrder(null);
      setHasSearched(false);
      setSearchResults([]);
      setHasPhoneSearched(false);
      setLastPhoneDistQuery('');
      setResultsTotal(0);
      setResultsPage(0);
      return;
    }

    if (trimmed.length < 5) {
      setError(t('Минимум 5 символов'));
      setOrder(null);
      setHasSearched(false);
      setSearchResults([]);
      setHasPhoneSearched(false);
      setLastPhoneDistQuery('');
      setResultsTotal(0);
      setResultsPage(0);
      return;
    }

    setError('');
    const hasLetters = /[a-zA-Zа-яА-Я]/.test(trimmed);
    if (hasLetters) {
      setSearchResults([]);
      setHasPhoneSearched(false);
      setResultsError('');
      setSelectedResultId(null);
      setLastPhoneDistQuery('');
      setResultsTotal(0);
      setResultsPage(0);
      await loadOrderDetails(trimmed);
      return;
    }

    setResultsError('');
    setHasPhoneSearched(true);
    setSelectedResultId(null);
    setResultsPage(0);
    setLastPhoneDistQuery(trimmed);
  };

  const handleOpenFromResult = async (result) => {
    if (!result) return;
    setSelectedResultId(result.id);
    setHasPhoneSearched(false);
    const number = result.external_order_id || String(result.id);
    await loadOrderDetails(number);
  };

  useEffect(() => {
    if (!hasPhoneSearched || !lastPhoneDistQuery) return;

    let isActive = true;
    const loadResults = async () => {
      if (!user?.api_token) {
        setResultsError(t('Нет доступа'));
        return;
      }
      setResultsLoading(true);
      setResultsError('');
      try {
        const res = await fetch(`/api/orders/search?phone_dist=${encodeURIComponent(lastPhoneDistQuery)}&page=${resultsPage + 1}&per_page=${resultsPerPage}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${user.api_token}`,
            'Accept-Language': getAcceptLanguageHeader(),
          },
        });
        const payload = await res.json().catch(() => ({}));
        if (res.status === 401 || res.status === 403) {
          logout();
          throw new Error(t('Нет доступа'));
        }
        if (!res.ok) {
          throw new Error(payload.error || t('Ошибка поиска заказа'));
        }
        if (!isActive) return;
        const orders = Array.isArray(payload.orders) ? payload.orders : [];
        setSearchResults(orders);
        const total = Number(payload?.pagination?.total);
        setResultsTotal(Number.isFinite(total) ? total : orders.length);
      } catch (err) {
        if (!isActive) return;
        setSearchResults([]);
        setResultsError(err?.message || t('Ошибка поиска заказа'));
        setResultsTotal(0);
      } finally {
        if (isActive) {
          setResultsLoading(false);
        }
      }
    };

    loadResults();
    return () => {
      isActive = false;
    };
  }, [hasPhoneSearched, lastPhoneDistQuery, resultsPage, resultsPerPage, user?.api_token, logout, t]);

  const handleResultsPageChange = (event, newPage) => {
    setResultsPage(newPage);
  };

  const handleResultsPerPageChange = (event) => {
    const value = Number(event.target.value) || 20;
    setResultsPerPage(value);
    setResultsPage(0);
  };

  useEffect(() => {
    if (!order) {
      setNotifyEmail('');
      return;
    }
    setNotifyEmail(order.email || '');
  }, [order]);

  const handleNotify = async () => {
    if (!order?.id) {
      setNotifySeverity('error');
      setNotifyMessage(t('Заказ не найден'));
      return;
    }
    const trimmedEmail = notifyEmail.trim();
    if (!trimmedEmail) {
      setNotifySeverity('error');
      setNotifyMessage(t('Введите email для отправки'));
      return;
    }
    if (!user?.api_token) {
      setNotifySeverity('error');
      setNotifyMessage(t('Нет доступа'));
      return;
    }
    setNotifyLoading(true);
    setNotifyMessage('');
    try {
      const res = await fetch(`/api/orders/${order.id}/notify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.api_token}`,
          'Accept-Language': getAcceptLanguageHeader(),
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });
      const payload = await res.json().catch(() => ({}));
      if (res.status === 401 || res.status === 403) {
        logout();
        throw new Error(t('Нет доступа'));
      }
      if (!res.ok) {
        throw new Error(payload.error || t('Ошибка отправки письма'));
      }
      setNotifySeverity('success');
      setNotifyMessage(t('Письмо отправлено'));
    } catch (err) {
      setNotifySeverity('error');
      setNotifyMessage(err?.message || t('Ошибка отправки письма'));
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleDownloadServiceAct = async () => {
    if (!order?.id) {
      setNotifySeverity('error');
      setNotifyMessage(t('Заказ не найден'));
      return;
    }
    if (!user?.api_token) {
      setNotifySeverity('error');
      setNotifyMessage(t('Нет доступа'));
      return;
    }

    setServiceActLoading(true);
    setNotifyMessage('');
    try {
      const res = await fetch(`/api/orders/${order.id}/service-act.pdf?lang=${encodeURIComponent(serviceActLang)}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${user.api_token}`,
          'Accept-Language': getAcceptLanguageHeader(),
        },
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        throw new Error(t('Нет доступа'));
      }
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || t('Ошибка скачивания PDF'));
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const orderName = order.external_order_id || order.id || 'order';
      link.href = url;
      link.download = `service-act-${orderName}-${serviceActLang}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setNotifySeverity('success');
      setNotifyMessage(t('Акт PDF скачан'));
    } catch (err) {
      setNotifySeverity('error');
      setNotifyMessage(err?.message || t('Ошибка скачивания PDF'));
    } finally {
      setServiceActLoading(false);
    }
  };

  const handleCopy = async (value) => {
    if (value == null) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopySnackbarOpen(true);
    } catch (err) {
      // ignore clipboard errors
    }
  };

  const resultsCount = resultsTotal || searchResults.length;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mb: 2 }}>
          {t('Найти заказ')}
        </Typography>
        <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label={t('Номер заказа или пополнения')}
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder={t('Введите номер заказа')}
            fullWidth
            sx={{ maxWidth: 420 }}
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={<SearchIcon />}
            sx={{ minWidth: 160, fontWeight: 600 }}
            disabled={loading}
          >
            {loading ? t('Поиск...') : t('Найти')}
          </Button>
          {order && (
            <>
              <TextField
                label={t('Email для отправки')}
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                type="email"
                fullWidth
                sx={{ maxWidth: 320 }}
              />
              <Button
                type="button"
                variant="outlined"
                onClick={handleNotify}
                disabled={notifyLoading}
                sx={{ minWidth: 260, fontWeight: 600 }}
              >
                {notifyLoading ? t('Отправка...') : t('Оповестить пользователя о статусе')}
              </Button>
              <TextField
                select
                label={t('Язык PDF')}
                value={serviceActLang}
                onChange={(e) => setServiceActLang(e.target.value)}
                size="small"
                sx={{ minWidth: 150 }}
              >
                {PDF_ACT_LANGS.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {t(item.label)}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                type="button"
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleDownloadServiceAct}
                disabled={serviceActLoading}
                sx={{ minWidth: 190, fontWeight: 600 }}
              >
                {serviceActLoading ? t('Генерация...') : t('Скачать акт PDF')}
              </Button>
            </>
          )}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {notifyMessage && (
          <Alert severity={notifySeverity} sx={{ mt: 2 }}>
            {notifyMessage}
          </Alert>
        )}
      </Paper>

      {(resultsLoading || resultsError || hasPhoneSearched) && (
        <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mb: 2 }}>
            {t('Результаты поиска')}
          </Typography>
          {resultsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resultsError}
            </Alert>
          )}
          {resultsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography>{t('Загрузка...')}</Typography>
            </Box>
          )}
          {!resultsLoading && !resultsError && searchResults.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{t('Номер заказа')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('Номер пополнения')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('Статус')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('Сумма пополнения')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('Дата и время оплаты')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((result, index) => (
                    <TableRow
                      key={result.id || `${result.external_order_id}-${index}`}
                      hover
                      onClick={() => handleOpenFromResult(result)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: index % 2 === 0 ? 'transparent' : '#fafafa',
                        ...(selectedResultId === result.id ? { backgroundColor: 'rgba(25, 118, 210, 0.08)' } : {}),
                      }}
                    >
                      <TableCell>{formatValue('external_order_id', result.external_order_id)}</TableCell>
                      <TableCell>{formatValue('phone_dist', result.phone_dist)}</TableCell>
                      <TableCell>{formatValue('order_status', result.order_status)}</TableCell>
                      <TableCell>{formatValue('amount_dist', result.amount_dist)}</TableCell>
                      <TableCell>{formatValue('payment_time', result.payment_time)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!resultsLoading && !resultsError && searchResults.length > 0 && (
            <TablePagination
              component="div"
              count={resultsCount}
              page={resultsPage}
              onPageChange={handleResultsPageChange}
              rowsPerPage={resultsPerPage}
              onRowsPerPageChange={handleResultsPerPageChange}
              rowsPerPageOptions={[20, 50, 100]}
              labelRowsPerPage={t('Строк на странице:')}
            />
          )}
          {!resultsLoading && !resultsError && hasPhoneSearched && searchResults.length === 0 && (
            <Typography color="text.secondary">{t('Нет результатов по номеру пополнения')}</Typography>
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mb: 2 }}>
              {t('Информация о заказе')}
            </Typography>
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} />
                <Typography>{t('Загрузка...')}</Typography>
              </Box>
            )}
            {!loading && order && (
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow
                        key={row.key}
                        hover
                        sx={{ bgcolor: index % 2 === 0 ? 'transparent' : '#fafafa' }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: '#1976d2', width: '35%' }}>
                          {row.label}
                        </TableCell>
                        <TableCell
                          sx={{ color: '#222', cursor: 'pointer' }}
                          onClick={() => handleCopy(row.value)}
                          title={t('Кликните для копирования')}
                        >
                          {row.value}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!loading && !order && hasSearched && !error && (
              <Typography color="text.secondary">{t('Заказ не найден')}</Typography>
            )}
            {!loading && !order && !hasSearched && !error && (
              <Typography color="text.secondary">{t('Введите номер заказа')}</Typography>
            )}
          </Paper>

          {!loading && order && extraRows.length > 0 && (
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mb: 2 }}>
                {t('Дополнительные поля')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {extraRows.map((row, index) => (
                      <TableRow
                        key={row.key}
                        hover
                        sx={{ bgcolor: index % 2 === 0 ? 'transparent' : '#fafafa' }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: '#1976d2', width: '35%' }}>
                          {row.label}
                        </TableCell>
                        <TableCell
                          sx={{ color: '#222', cursor: 'pointer' }}
                          onClick={() => handleCopy(row.value)}
                          title={t('Кликните для копирования')}
                        >
                          {row.value}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
        <Box
          sx={{
            width: '30%',
            minWidth: 320,
            alignSelf: 'flex-start',
          }}
        >
          {!loading && order && (
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mb: 2 }}>
                {t('Чек')}
              </Typography>
              <OrderReceipt order={order} />
            </Paper>
          )}
        </Box>
      </Box>
      </Box>
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={1200}
        onClose={() => setCopySnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setCopySnackbarOpen(false)}>
          {t('Скопировано!')}
        </Alert>
      </Snackbar>
    </>
  );
}
